import React, { useState, useEffect } from "react";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useExperienceRatingApi } from "src/_common/hooks/actions";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import "./ExperienceRatingModal.css";

interface ExperienceRatingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ExperienceRatingModal({ isOpen, onClose }: ExperienceRatingModalProps) {
  const { t } = useTranslation("experienceRating");
  const { callAddExperienceRating, callGetExperienceRating, callUpdateExperienceRating } = useExperienceRatingApi();
  const toast = useToaster();

  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [comment, setComment] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [existingRating, setExistingRating] = useState<any>(null);

  // Fetch existing rating when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchExistingRating();
    } else {
      // Reset form when modal closes
      setRating(0);
      setComment("");
      setExistingRating(null);
    }
  }, [isOpen]);

  const fetchExistingRating = () => {
    callGetExperienceRating(
      {},
      (message: string, response: any) => {
        if (response && response.experience) {
          setExistingRating(response);
          setRating(response.experience || 0);
          setComment(response.experience_comment || "");
        }
      },
      (error: any) => {
        console.log("No existing rating found or error:", error);
      },
    );
  };

  const handleStarClick = (starIndex: number, event: React.MouseEvent<HTMLSpanElement>) => {
    const starElement = event.currentTarget;
    const rect = starElement.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const starWidth = rect.width;

    // Divide each star into 5 segments: 0.2, 0.4, 0.6, 0.8, 1.0
    const segmentWidth = starWidth / 5;
    const segment = Math.ceil(clickX / segmentWidth);

    // Calculate the rating: (starIndex - 1) + (segment * 0.2)
    const newRating = starIndex - 1 + segment * 0.2;

    setRating(newRating);
  };

  const handleStarHover = (starIndex: number, event: React.MouseEvent<HTMLSpanElement>) => {
    const starElement = event.currentTarget;
    const rect = starElement.getBoundingClientRect();
    const hoverX = event.clientX - rect.left;
    const starWidth = rect.width;

    // Divide each star into 5 segments: 0.2, 0.4, 0.6, 0.8, 1.0
    const segmentWidth = starWidth / 5;
    const segment = Math.ceil(hoverX / segmentWidth);

    // Calculate the hover rating: (starIndex - 1) + (segment * 0.2)
    const hoverRating = starIndex - 1 + segment * 0.2;

    setHoveredRating(hoverRating);
  };

  const handleSubmit = () => {
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    setIsLoading(true);

    const data = {
      experience: rating,
      experience_comment: comment.trim(),
    };

    const apiCall = existingRating ? callUpdateExperienceRating : callAddExperienceRating;
    const successMessage = existingRating ? "Rating updated successfully" : "Rating submitted successfully";

    apiCall(
      data,
      (message: string, response: any) => {
        setIsLoading(false);
        toast.success(successMessage);
        onClose();
      },
      (error: any) => {
        setIsLoading(false);
        toast.error("Failed to submit rating. Please try again.");
      },
    );
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="experience-rating-backdrop" onClick={onClose}></div>

      {/* Modal */}
      <div className="experience-rating-modal">
        <div className="modal-header">
          <h3>{t("subtitle")}</h3>
        </div>

        <div className="modal-body">
          <div className="rating-section">
            <p className="rating-question">{t("question")}</p>
            <div className="stars-container">
              {[1, 2, 3, 4, 5].map((star) => {
                const displayValue = hoveredRating || rating;
                const fillPercentage =
                  displayValue >= star ? 100 : displayValue > star - 1 ? (displayValue - (star - 1)) * 100 : 0;

                return (
                  <span
                    key={star}
                    className="star-wrapper"
                    onClick={(e) => handleStarClick(star, e)}
                    onMouseMove={(e) => handleStarHover(star, e)}
                    onMouseLeave={() => setHoveredRating(0)}
                  >
                    {fillPercentage > 0 && fillPercentage < 100 ? (
                      // Partial star (gradient fill)
                      <span className="star-container">
                        <span className="star star-empty">★</span>
                        <span className="star-fill" style={{ width: `${fillPercentage}%` }}>
                          <span className="star star-filled">★</span>
                        </span>
                      </span>
                    ) : (
                      // Full or empty star
                      <span className={`star ${star <= displayValue ? "filled" : ""}`}>★</span>
                    )}
                  </span>
                );
              })}
            </div>
            <div className="rating-value">{rating > 0 ? `${rating.toFixed(1)} / 5` : ""}</div>
          </div>

          <div className="comment-section">
            <p className="comment-label">{t("commentLabel")}</p>
            <p className="comment-sublabel">{t("commentSublabel")}</p>
            <textarea
              className="comment-input"
              placeholder={t("commentPlaceholder")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              maxLength={500}
            />
          </div>

          <button className="submit-button" onClick={handleSubmit} disabled={isLoading || rating === 0}>
            {isLoading ? t("submitting") : t("submit")}
          </button>
        </div>
      </div>
    </>
  );
}
